import React from 'react'
import { Box, Typography, Stack, Tooltip, useTheme, styled } from '@mui/material'
import Title from 'components/global/Title'
import CardActionsButtonsGroup from 'components/global/CardActions/CardActionsButtonsGroup'
import CardActionButton from 'components/global/CardActions/CardActionButton'
import CardSkeleton from 'components/global/CUK/Skeletons/CardSkeleton'
import { useCardPreviewLayout } from './CardPreviewLayoutContext'

export const CardHeaderTitle = styled(Typography)(() => ({
  display: 'flex',
  justifyContent: 'center',
  fontFamily: 'Poppins',
  fontWeight: 500,
  fontSize: 16,
  lineHeight: 2,
  color: '#5D5F70',
}))

export const CardHeaderEmailLink = styled('span')(({ theme }) => ({
  borderBottom: '1px dashed #eeeeee',
  transition: '.3s',
  cursor: 'pointer',
  '&:hover': {
    borderBottomColor: theme.palette.primary.main,
  },
}))

interface ICardBlockInfoProps {
  title: string
  subtitle?: string | number | JSX.Element | null
  tooltiptitle?: string | null
  children?: React.ReactNode
}

export const CardBlockInfo = ({ title, subtitle = null, tooltiptitle = null , children = null }:ICardBlockInfoProps) => {
  if (subtitle === null && children === null) {
    return null
  }

  return (
    <Tooltip title={tooltiptitle} arrow placement="top">
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          width: '100%',
          gridColumn: '1 / -1',
          minWidth: 0
        }}
      >
        <Typography sx={{ color: '#464646', fontSize: 12, fontWeight: 500, flex: '0 0 auto', maxWidth: '45%' }}>
          {title}
        </Typography>
        <Box sx={{ flex: '1 1 auto', minWidth: 8, borderTop: '1px solid #F7F7F7' }} />
        <Box
          sx={{
            color: '#656565',
            fontSize: 12,
            fontWeight: 400,
            flex: '0 1 auto',
            maxWidth: '55%',
            minWidth: 0,
            textAlign: 'right',
            wordBreak: 'break-word',
            '& *': {
              color: '#656565',
              fontSize: 12,
              fontWeight: 400
            }
          }}
        >
          {children || subtitle}
        </Box>
      </Box>
    </Tooltip>
  )
}

export const CardBody = ({ grid = false, children }) => {
  const theme = useTheme()
  return (
    <Box
      sx={{
        display: grid ? 'grid' : 'flex',
        flexDirection: grid ? undefined : 'column',
        gridTemplateColumns: '1fr',
        gap: theme.spacing(1.5),
        flex: '1 1 auto',
        minHeight: 0,
        overflowY: 'auto',
        overflowX: 'hidden',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        '&::-webkit-scrollbar': {
          display: 'none'
        }
      }}>
      {children}
    </Box>
  )
}

export const CardHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  paddingBottom: theme.spacing(2)
}))

const hiddenScrollbarSx = {
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
  '&::-webkit-scrollbar': {
    display: 'none'
  }
}

interface ICardPreviewProps {
  title: string
  children?: React.ReactNode
  error?: string | null | JSX.Element
  loading?: boolean
  onClose?: () => void
  padding?: number | string
  components?: {ActionButtons?: React.FC}
  hideHeader?: boolean
}

const CardPreview = ({
  title,
  components = {},
  children,
  error,
  loading = false,
  onClose,
  padding = 3,
  hideHeader = false
}:ICardPreviewProps) => {
  const cardPreviewLayout = useCardPreviewLayout()
  const isSlideInLayout = cardPreviewLayout === 'slideIn'

  return (
    <Stack
      gap={isSlideInLayout ? 2 : 3}
      flexGrow={1}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        minHeight: 0,
        height: '100%',
        maxHeight: '100%',
        boxSizing: 'border-box',
        border: isSlideInLayout ? 'none' : '1px solid #EFEFEF',
        borderRadius: isSlideInLayout ? 0 : '24px',
        padding: isSlideInLayout ? 0 : padding,
        width: '100%',
        minWidth: isSlideInLayout ? 0 : '300px',
        backgroundColor: isSlideInLayout ? 'transparent' : '#FFFFFF'
      }}>
      {!hideHeader && (
        <Box display={'flex'} alignItems={'center'} sx={{ flexShrink: 0 }}>
          <Title titleKey={title} lineless={true} bulletless={true}>
            <CardActionsButtonsGroup>
              {components.ActionButtons && <components.ActionButtons />}
              <CardActionButton
                iconweight={'regular'}
                iconlabel={'xmark'}
                onClick={onClose}
                style={{
                  color: '#656565',
                  fontSize: '12px',
                  minWidth: 16,
                  minHeight: 16
                }}
              />
            </CardActionsButtonsGroup>
          </Title>
        </Box>
      )}
      <Box
        sx={{
          flex: '1 1 auto',
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          ...hiddenScrollbarSx
        }}
      >
        {loading ? <CardSkeleton /> : error ? <h1>{error}</h1> : children}
      </Box>
    </Stack>
  )
}

export default CardPreview
